// src/context/UserContext.js
import { createContext, useContext, useState, useEffect } from "react";

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  /**
   * orgViewMode — Super Admin "View as Organization" feature.
   * When set, the Super Admin is browsing as if they were an org admin.
   * Shape: { orgId: number, orgName: string, orgLogo: string|null }
   * null means Super Admin is in their normal full-access view.
   */
  const [orgViewMode, setOrgViewMode] = useState(null);

  // Load user from localStorage on first load
  useEffect(() => {
    const storedUser = localStorage.getItem("userInfo");
    let isSwitchedSuperAdmin = false;
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser(parsed);
        isSwitchedSuperAdmin = Boolean(
          parsed.is_switched_from_super_admin ||
          localStorage.getItem('gpn_sa_token') ||
          sessionStorage.getItem('gpn_sa_token')
        );
      } catch {}
    }

    // Only restore or set orgViewMode if the user is a Super Admin who has switched into an org
    if (isSwitchedSuperAdmin) {
      // Restore SA backup session if stored in localStorage
      const savedSaToken = localStorage.getItem("gpn_sa_token");
      if (savedSaToken && !sessionStorage.getItem("gpn_sa_token")) {
        sessionStorage.setItem("gpn_sa_token", savedSaToken);
      }
      const savedSaUserInfo = localStorage.getItem("gpn_sa_userInfo");
      if (savedSaUserInfo && !sessionStorage.getItem("gpn_sa_userInfo")) {
        sessionStorage.setItem("gpn_sa_userInfo", savedSaUserInfo);
      }

      const storedOrgView = sessionStorage.getItem("gpn_org_view_mode") || localStorage.getItem("gpn_org_view_mode");
      if (storedOrgView) {
        try {
          const parsedOrg = JSON.parse(storedOrgView);
          setOrgViewMode(parsedOrg);
        } catch {}
      } else if (storedUser) {
        try {
          const parsed = JSON.parse(storedUser);
          if (parsed.org_id) {
            const autoMode = {
              orgId: parsed.org_id,
              orgName: parsed.organization || 'Organization',
              orgLogo: parsed.organization_logo || null,
            };
            setOrgViewMode(autoMode);
            sessionStorage.setItem("gpn_org_view_mode", JSON.stringify(autoMode));
            localStorage.setItem("gpn_org_view_mode", JSON.stringify(autoMode));
          }
        } catch {}
      }
    } else {
      // For standard users / single org admins: purge any stale orgViewMode markers
      sessionStorage.removeItem("gpn_org_view_mode");
      localStorage.removeItem("gpn_org_view_mode");
      sessionStorage.removeItem("gpn_sa_token");
      sessionStorage.removeItem("gpn_sa_userInfo");
      localStorage.removeItem("gpn_sa_token");
      localStorage.removeItem("gpn_sa_userInfo");
      setOrgViewMode(null);
    }
  }, []);

  const handleSetOrgViewMode = (mode) => {
    setOrgViewMode(mode);
    if (mode) {
      sessionStorage.setItem("gpn_org_view_mode", JSON.stringify(mode));
      localStorage.setItem("gpn_org_view_mode", JSON.stringify(mode));
    } else {
      sessionStorage.removeItem("gpn_org_view_mode");
      localStorage.removeItem("gpn_org_view_mode");
    }
  };

  const storedOrg = (() => {
    try {
      const raw = sessionStorage.getItem("gpn_org_view_mode") || localStorage.getItem("gpn_org_view_mode");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  const isSwitched = Boolean(
    user?.is_switched_from_super_admin ||
    sessionStorage.getItem("gpn_sa_token") ||
    localStorage.getItem("gpn_sa_token")
  );

  const effectiveOrgId = (isSwitched && (orgViewMode?.orgId || storedOrg?.orgId)) || (user?.org_id ? user?.org_id : null);

  return (
    <UserContext.Provider value={{ user, setUser, orgViewMode, setOrgViewMode: handleSetOrgViewMode, isSwitched, effectiveOrgId }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
